import type { AuthUser } from "../types/auth";
import type { BudgetAuthorityConfigEntry, BudgetType } from "../types/budget";
import type { RequisitionDepartment, RequisitionDetail, RequisitionOrigin } from "../types/requisition";
import { FORM_IDS, PROCESS_IDS } from "./permissionCatalog";
import { hasAnyRole, hasFormPermission, hasPermission, hasProcessPermission } from "./permissions";
//updated on 05-06

function isOfficeUser(user: AuthUser | null | undefined): boolean {
  return user?.type === "office";
}

function isCrewUser(user: AuthUser | null | undefined): boolean {
  return user?.type === "crew";
}

function normalizeActorId(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
}

export function resolveAuditActorId(user: AuthUser | null | undefined): string | null {
  if (!user) {
    return null;
  }

  if (user.type === "crew") {
    return (
      normalizeActorId(user.crew_id)
      || normalizeActorId(user.employee_id)
      || normalizeActorId(user.id)
    );
  }

  return (
    normalizeActorId(user.employee_id)
    || normalizeActorId(user.crew_id)
    || normalizeActorId(user.id)
  );
}

export function canEditReturnedDraft(
  user: AuthUser | null | undefined,
  requisition: Pick<RequisitionDetail, "status" | "draft_edit_locked" | "draft_edit_owner_actor_id"> | null | undefined,
): boolean {
  if (!requisition || requisition.status !== "DRAFT") {
    return false;
  }

  if (!requisition.draft_edit_locked) {
    return true;
  }

  const currentActorId = resolveAuditActorId(user);
  const ownerActorId = normalizeActorId(requisition.draft_edit_owner_actor_id);
  return currentActorId !== null && currentActorId === ownerActorId;
}

export function canRejectReturnedDraftToPreviousEditor(
  user: AuthUser | null | undefined,
  requisition:
    | Pick<
        RequisitionDetail,
        "status" | "draft_edit_locked" | "draft_edit_owner_actor_id" | "draft_reject_target_actor_id"
      >
    | null
    | undefined,
): boolean {
  if (!requisition || requisition.status !== "DRAFT" || !requisition.draft_edit_locked) {
    return false;
  }

  const currentActorId = resolveAuditActorId(user);
  const ownerActorId = normalizeActorId(requisition.draft_edit_owner_actor_id);
  if (currentActorId === null || currentActorId !== ownerActorId) {
    return false;
  }

  return Boolean(requisition.draft_reject_target_actor_id);
}

export function canViewRFQs(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasFormPermission(user, [FORM_IDS.RFQS]);
}

export function canAccessScopedRFQComparison(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasProcessPermission(user, [PROCESS_IDS.RFQ_COMPARISON_VIEW]);
}

export function canAwardRFQs(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasProcessPermission(user, [PROCESS_IDS.RFQ_AWARD]);
}

export function canViewRFQWorklist(user: AuthUser | null | undefined): boolean {
  return canViewRFQs(user) || canAccessScopedRFQComparison(user);
}

export function canManageRFQs(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.RFQS],
    processes: [PROCESS_IDS.RFQ_MANAGE],
  });
}

export function canCategoryReviewRequisitions(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    processes: [PROCESS_IDS.REQUISITION_CATEGORY_REVIEW],
  });
}

export function canManageApprovalChains(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) &&
    hasPermission(user, {
      forms: [FORM_IDS.APPROVAL_CHAINS],
      processes: [PROCESS_IDS.APPROVAL_CHAIN_MANAGE],
    });
}

export function canViewVendors(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, { forms: [FORM_IDS.VENDORS] });
}

export function canManageVendors(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.VENDORS],
    processes: [PROCESS_IDS.VENDOR_MANAGE],
  });
}

export function canApproveVendors(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.VENDORS],
    processes: [PROCESS_IDS.VENDOR_APPROVE],
  });
}

export function canManageVendorBankDetails(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) &&
    hasPermission(user, {
      forms: [FORM_IDS.VENDORS],
      processes: [PROCESS_IDS.VENDOR_MANAGE],
    });
}

export function canViewItems(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, { forms: [FORM_IDS.ITEMS] });
}

export function canManageInventoryAdministration(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.INVENTORY],
    processes: [PROCESS_IDS.INVENTORY_ADMIN_MANAGE],
  });
}

export function canManageItems(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.ITEMS],
    processes: [PROCESS_IDS.ITEM_MANAGE],
  });
}

export function canAssignItemAccountCodes(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) &&
    hasPermission(user, {
      forms: [FORM_IDS.ITEMS],
      processes: [PROCESS_IDS.ITEM_ACCOUNT_CODE_ASSIGN],
    });
}

export function canApproveItems(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.ITEMS],
    processes: [PROCESS_IDS.ITEM_APPROVE],
  });
}

export function canViewServices(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, { forms: [FORM_IDS.SERVICES] });
}

export function canManageServices(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.SERVICES],
    processes: [PROCESS_IDS.SERVICE_MANAGE],
  });
}

export function canApproveServices(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.SERVICES],
    processes: [PROCESS_IDS.SERVICE_APPROVE],
  });
}

export function canManageBudgets(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.BUDGETS],
    processes: [PROCESS_IDS.BUDGET_MANAGE],
  });
}

export function canViewBudgets(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasFormPermission(user, [FORM_IDS.BUDGETS]);
}

export function canViewBudgetWorksheets(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasFormPermission(user, [FORM_IDS.BUDGETS]);
}

export function canManageProjects(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.PROJECTS],
    processes: [PROCESS_IDS.PROJECT_MANAGE],
  });
}

export function canCreateBudgetType(
  user: AuthUser | null | undefined,
  budgetType: BudgetType,
  authorityConfig: BudgetAuthorityConfigEntry[] | null | undefined,
): boolean {
  if (!canManageBudgets(user) || !authorityConfig?.length) {
    return false;
  }

  const allowedRoleCodes = authorityConfig
    .filter((entry) => entry.action === "CREATE" && entry.budget_type === budgetType)
    .map((entry) => entry.role_code);

  return allowedRoleCodes.length > 0 && hasAnyRole(user, allowedRoleCodes);
}

export function canAccessBudgetAllocation(
  user: AuthUser | null | undefined,
  authorityConfig: BudgetAuthorityConfigEntry[] | null | undefined,
): boolean {
  if (!isOfficeUser(user) || !hasPermission(user, {
    forms: [FORM_IDS.BUDGETS],
    processes: [PROCESS_IDS.BUDGET_ALLOCATE],
  })) {
    return false;
  }

  if (authorityConfig !== null && authorityConfig !== undefined) {
    return canCreateBudgetType(user, "OPEX", authorityConfig);
  }

  return true;
}

export function canActivateBudgetPlans(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.BUDGETS],
    processes: [PROCESS_IDS.BUDGET_ACTIVATE],
  });
}

export function canApproveBudgetPlans(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.BUDGETS],
    processes: [PROCESS_IDS.BUDGET_APPROVE],
  });
}

export function canCloseBudgetYears(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.BUDGETS],
    processes: [PROCESS_IDS.BUDGET_CLOSE],
  });
}

export function canReopenBudgetYears(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.BUDGETS],
    processes: [PROCESS_IDS.BUDGET_REOPEN],
  });
}

export function canManageAccountCodes(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.ACCOUNT_CODES],
    processes: [PROCESS_IDS.ACCOUNT_CODE_MANAGE],
  });
}

export function canCreateAccountCodes(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.ACCOUNT_CODES],
    processes: [PROCESS_IDS.ACCOUNT_CODE_CREATE],
  });
}

export function canViewAccountCodes(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.ACCOUNT_CODES],
    processes: [PROCESS_IDS.ACCOUNT_CODE_VIEW],
  });
}

export function canViewExchangeRates(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.EXCHANGE_RATES],
    processes: [PROCESS_IDS.EXCHANGE_RATE_VIEW, PROCESS_IDS.EXCHANGE_RATE_MANAGE],
  });
}

export function canManageExchangeRates(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.EXCHANGE_RATES],
    processes: [PROCESS_IDS.EXCHANGE_RATE_MANAGE],
  });
}

export function canCreateRecoveryEntries(user: AuthUser | null | undefined): boolean {
  return canManageBudgets(user);
}

export function canVerifyRecoveryEntries(user: AuthUser | null | undefined): boolean {
  return canManageBudgets(user);
}

export function canVerifyInvoices(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) &&
    hasPermission(user, {
      forms: [FORM_IDS.INVOICES],
      processes: [PROCESS_IDS.INVOICE_VERIFY],
    });
}

export function canViewInvoices(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasFormPermission(user, [FORM_IDS.INVOICES]);
}

export function canApproveInvoices(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) &&
    hasPermission(user, {
      forms: [FORM_IDS.INVOICES],
      processes: [PROCESS_IDS.INVOICE_APPROVE],
    });
}

export function canManageInvoiceConfiguration(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) &&
    hasPermission(user, {
      forms: [FORM_IDS.INVOICE_CONFIG],
      processes: [PROCESS_IDS.INVOICE_CONFIG_MANAGE],
    });
}

export function canViewCreditNotes(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) &&
    hasPermission(user, {
      forms: [FORM_IDS.CREDIT_NOTES],
      processes: [PROCESS_IDS.CREDIT_NOTE_VIEW],
    });
}

export function canCreateCreditNotes(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.CREDIT_NOTES],
    processes: [PROCESS_IDS.CREDIT_NOTE_CREATE],
  });
}

export function canVerifyCreditNotes(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.CREDIT_NOTES],
    processes: [PROCESS_IDS.CREDIT_NOTE_VERIFY],
  });
}

export function canApplyCreditNotes(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.CREDIT_NOTES],
    processes: [PROCESS_IDS.CREDIT_NOTE_APPLY],
  });
}

export function canCreateGrns(user: AuthUser | null | undefined): boolean {
  return isCrewUser(user) && hasPermission(user, {
    forms: [FORM_IDS.GRN],
    processes: [PROCESS_IDS.GRN_CREATE],
  });
}

export function canCreateServiceCompletions(user: AuthUser | null | undefined): boolean {
  return isCrewUser(user) && hasPermission(user, {
    forms: [FORM_IDS.TRACKER],
    processes: [PROCESS_IDS.SERVICE_COMPLETION_CREATE],
  });
}

function canCreateOfficeRequisitions(user: AuthUser | null | undefined): boolean {
  if (!isOfficeUser(user)) {
    return false;
  }

  return hasPermission(user, {
    forms: [FORM_IDS.REQUISITIONS],
    processes: [PROCESS_IDS.REQUISITION_CREATE],
  });
}

export function canSubmitRequisitions(
  user: AuthUser | null | undefined,
  origin?: RequisitionOrigin,
): boolean {
  if (origin === "OFFICE") {
    return canCreateOfficeRequisitions(user);
  }

  return hasPermission(user, { processes: [PROCESS_IDS.REQUISITION_CREATE] });
}

export function canHodReviewRequisition(
  user: AuthUser | null | undefined,
  department: RequisitionDepartment | null | undefined,
): boolean {
  if (!user || !department) {
    return false;
  }

  if (!hasPermission(user, { processes: [PROCESS_IDS.REQUISITION_HOD_REVIEW] })) {
    return false;
  }

  if (hasAnyRole(user, ["SUPER_ADMIN", "MASTER", "ACTING_MASTER", "VESSEL_OFFICER"])) {
    return true;
  }

  if (department === "DECK") {
    return hasAnyRole(user, ["CHIEF_OFFICER"]);
  }

  if (department === "ENGINE") {
    return hasAnyRole(user, ["CHIEF_ENGINEER"]);
  }

  return false;
}

export function canRequestBudgetRevisions(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.BUDGETS],
    processes: [PROCESS_IDS.BUDGET_REVISION_REQUEST],
  });
}

export function canApproveBudgetRevisions(user: AuthUser | null | undefined): boolean {
  return isOfficeUser(user) && hasPermission(user, {
    forms: [FORM_IDS.BUDGETS],
    processes: [PROCESS_IDS.BUDGET_REVISION_APPROVE],
  });
}
